import * as React from 'react';
import { Box, Grid } from '@mui/material';
import NumberFormat from 'react-number-format';
import {
	CerebroComplexListItem,
	CerebroDrawer,
	CerebroHeading,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroStyledBox,
	DebtCapitalHeading,
	CerebroForm,
} from '.';
import { ReactComponent as GrowthCapital } from '../icons/GrowthCapital.svg';
import { DealRequestAllocation } from '../models';
import { Field, Formik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../context';
import { useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { getApiResponseData } from '../utils/axios';
import { DebtCapitalSectionProps } from '../../pages';
import { CerebroApiEndpoints } from '../utils/api';
import { QueryKeys } from '../hooks';
import { HtmlUtils, NumberUtils } from '../utils';
import { MaxCurrencyLength } from '../utils/platform';

export const CerebroDebtGrowthCapital: React.FC<DebtCapitalSectionProps> = ({
	allocation,
	drawerOpenByDefault = false,
}) => {
	const [open, setOpen] = React.useState(drawerOpenByDefault);
	const api = useApi();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();
	const snackbar = useSnackbar();

	const isEmpty = isGrowthCapitalEmpty(allocation);

	const { mutate } = useMutation(
		async (values: DealRequestAllocationGrowthCapitalValues) => {
			const payload: Partial<DealRequestAllocation> = {
				totalGrowthCapitalHardCostAmount: NumberUtils.ParseDecimal(values.totalGrowthCapitalHardCostAmount),
				totalGrowthCapitalSoftCostAmount: NumberUtils.ParseDecimal(values.totalGrowthCapitalSoftCostAmount),
			};

			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), payload)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: data => {
				client.setQueryData(QueryKeys.Allocation(guid), data);

				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				setOpen(false);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
		}
	);

	const drawerRef = React.useRef<HTMLDivElement>(null);

	const amountRequested = NumberUtils.Sum(
		allocation.totalGrowthCapitalHardCostAmount,
		allocation.totalGrowthCapitalSoftCostAmount
	);

	return (
		<>
			<DebtCapitalHeading icon={GrowthCapital} amountRequested={amountRequested}>
				Growth Capital
			</DebtCapitalHeading>

			{isEmpty ? (
				<CerebroStyledBox sx={{ marginBottom: 8 }}>
					<CerebroHeading size="h2" component="h2">
						No details have been added yet.
					</CerebroHeading>
					<br />
					<br />
					<CerebroPrimaryButton wide onClick={() => setOpen(true)}>
						Add Growth Capital Details
					</CerebroPrimaryButton>
					<br />
				</CerebroStyledBox>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }} onEdit={() => setOpen(true)}>
					<Grid container>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>Hard Costs</CerebroLabel>
							<NumberFormat
								value={allocation.totalGrowthCapitalHardCostAmount}
								displayType="text"
								thousandSeparator={true}
								prefix="$"
							/>
						</Grid>
						<Grid item xs={6}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>Soft Costs</CerebroLabel>
							<NumberFormat
								value={allocation.totalGrowthCapitalSoftCostAmount}
								displayType="text"
								thousandSeparator={true}
								prefix="$"
							/>
						</Grid>
					</Grid>
				</CerebroComplexListItem>
			)}

			<CerebroDrawer ref={drawerRef} open={open} onClose={() => setOpen(false)} icon={GrowthCapital}>
				<Box sx={{ maxWidth: { xs: 'auto', md: 400 } }}>
					<CerebroHeading>Growth Capital</CerebroHeading>
					<br />
					<Formik
						initialValues={{
							totalGrowthCapitalHardCostAmount: NumberUtils.FormatCurrency(
								allocation.totalGrowthCapitalHardCostAmount
							),
							totalGrowthCapitalSoftCostAmount: NumberUtils.FormatCurrency(
								allocation.totalGrowthCapitalSoftCostAmount
							),
						}}
						validationSchema={ValidationSchema}
						onSubmit={values => mutate(values)}
					>
						{({ isSubmitting, errors, touched, handleSubmit, values }) => {
							return (
								<CerebroForm
									flatIndicator
									onSubmit={handleSubmit}
									scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(drawerRef.current)}
								>
									<Box sx={{ marginBottom: 2 }}>
										<CerebroLabel
											gutterBottom
											required
											htmlFor="totalGrowthCapitalHardCostAmount"
											error={
												touched.totalGrowthCapitalHardCostAmount &&
												errors.totalGrowthCapitalHardCostAmount != null
											}
										>
											Hard Cost Amount
										</CerebroLabel>
										<CerebroText variant="hint" gutterBottom>
											Examples: Leasehold Improvements, Furniture and Fixtures, Cost of new
											Software/Hardware
										</CerebroText>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="totalGrowthCapitalHardCostAmount"
											name="totalGrowthCapitalHardCostAmount"
											disabled={isSubmitting}
											value={values.totalGrowthCapitalHardCostAmount}
											error={
												touched.totalGrowthCapitalHardCostAmount &&
												errors.totalGrowthCapitalHardCostAmount != null
											}
										/>
										{touched.totalGrowthCapitalHardCostAmount &&
											errors.totalGrowthCapitalHardCostAmount != null && (
												<CerebroText variant="error">
													{errors.totalGrowthCapitalHardCostAmount}
												</CerebroText>
											)}
									</Box>
									<Box sx={{ marginBottom: 2 }}>
										<CerebroLabel
											gutterBottom
											required
											htmlFor="totalGrowthCapitalSoftCostAmount"
											error={
												touched.totalGrowthCapitalSoftCostAmount &&
												errors.totalGrowthCapitalSoftCostAmount != null
											}
										>
											Soft Cost Amount
										</CerebroLabel>
										<CerebroText variant="hint" gutterBottom>
											Examples: Research and Development, Marketing/Business Development, Hiring,
											General Corporate Purposes
										</CerebroText>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="totalGrowthCapitalSoftCostAmount"
											name="totalGrowthCapitalSoftCostAmount"
											disabled={isSubmitting}
											value={values.totalGrowthCapitalSoftCostAmount}
											error={
												touched.totalGrowthCapitalSoftCostAmount &&
												errors.totalGrowthCapitalSoftCostAmount != null
											}
										/>
										{touched.totalGrowthCapitalSoftCostAmount &&
											errors.totalGrowthCapitalSoftCostAmount != null && (
												<CerebroText variant="error">
													{errors.totalGrowthCapitalSoftCostAmount}
												</CerebroText>
											)}
									</Box>
									<Grid container justifyContent="space-between" sx={{ marginTop: 5 }}>
										<Grid item>
											<CerebroPrimaryButton loading={isSubmitting} type="submit">
												Save Details
											</CerebroPrimaryButton>
										</Grid>
									</Grid>
								</CerebroForm>
							);
						}}
					</Formik>
				</Box>
			</CerebroDrawer>
		</>
	);
};

interface DealRequestAllocationGrowthCapitalValues {
	totalGrowthCapitalHardCostAmount?: string;
	totalGrowthCapitalSoftCostAmount?: string;
}

const ValidationSchema = Yup.object().shape({
	totalGrowthCapitalHardCostAmount: Yup.string().required('Hard Cost Amount is required!'),
	totalGrowthCapitalSoftCostAmount: Yup.string().required('Soft Cost Amount is required!'),
});

export function isGrowthCapitalEmpty(allocation: DealRequestAllocation): boolean {
	return !allocation.totalGrowthCapitalHardCostAmount && !allocation.totalGrowthCapitalSoftCostAmount;
}
